import React, { useState } from 'react'
import { PayPalCheckout, paypalCheckout } from 'braintree-web'
import { AuthorizationResponse } from 'paypal-checkout-components'
import { useBraintree } from './use-braintree';
import { useBasketStore, useGlobalStore, useOrderStore } from '../store/hooks/useStore';
import { ButtonsRendererOptions, PaymentMethodSetupData, ButtonLabelOption, ButtonShapeOption, ButtonColorOption, FlowType, Intent } from '../types/payments';
import { PAYMENT_METHOD_IDS } from '../utils/constants';

export function useBraintreePaypal() {
  const {
    currency,
  } = useGlobalStore()
  const { createBraintreeClientInstance } = useBraintree()
  const orderStore = useOrderStore()
  const { shippingAddress } = useBasketStore()
  const [hasPaypalButtonRendered, setHasPaypalButtonRendered] = useState<boolean>(false)

  const initPaypalButtons = async (data: PaymentMethodSetupData) => {
    if (shippingAddress && data.billingAddress) {
      await createBraintreePaypal(data)
    }
  }

  const createBraintreePaypalInstance = async () => {
      const { btClientInstance } = await createBraintreeClientInstance()

      const instance = await paypalCheckout.create({
        client: btClientInstance,
      })

    return instance
  }

  const getPaypalConfig = async (ppData: PaymentMethodSetupData, instance: PayPalCheckout) => {
    const { amount, billingAddress, setErrorMessage, submitOrderProcess, isTopUp } = ppData
    const paypalButtonConfig: ButtonsRendererOptions = ({
      fundingSource: 'paypal',
      style: {
        label: ButtonLabelOption.Pay,
        height: 44,
        shape: ButtonShapeOption.Rect,
        color: ButtonColorOption.Blue
      },
      createOrder: () => {
        return instance.createPayment({
          flow: FlowType.Checkout,
          amount: isTopUp ? orderStore.topupAmount : amount,
          currency: currency,
          locale: 'en_GB',
          intent: Intent.Authorize,
          enableShippingAddress: true,
          shippingAddressEditable: false,
          shippingAddressOverride: {
            recipientName: shippingAddress?.fullName,
            line1: shippingAddress?.address1 || '',
            line2: shippingAddress?.address2 || '',
            city: shippingAddress?.city || '',
            countryCode: shippingAddress?.countryCode?.toUpperCase() || 'GB',
            postalCode: shippingAddress?.postalCode || '',
            state: shippingAddress?.stateCode || '',
            phone: shippingAddress?.phone || '',
          },
        });
      },
      onApprove: (data) => {
        return new Promise<AuthorizationResponse>((resolve, reject) => {
          instance.tokenizePayment(data, async (tokenizeErr, payload) => {
            try {
              if (payload.nonce && billingAddress) {
                await submitOrderProcess(payload.nonce, payload.type, billingAddress, PAYMENT_METHOD_IDS.PAYPAL, payload.details.email);
                resolve(payload);
              }
            } catch (error) {
              console.error(error);
              setErrorMessage(error)
              reject(error);
              return;
            }
            if (tokenizeErr) {
              console.error('Error tokenizing payment:', tokenizeErr);
              setErrorMessage('Error tokenizing payment: ' + tokenizeErr)
              reject(tokenizeErr);
              return;
            }
            resolve(payload);
          });
        });
      },
      onCancel: (data) => {
        console.log('PayPal payment cancelled', JSON.stringify(data, null, 2));
      },
      onError: (err) => {
        console.error('PayPal error', err);
        setErrorMessage('PayPal error ' + err)
      },
    })

    return paypalButtonConfig
  }

  const renderPayPalButtons = async (ppData: PaymentMethodSetupData, instance: PayPalCheckout) => {
    const { setErrorMessage } = ppData

    const paypalButtonConfig = await getPaypalConfig(ppData, instance)

    const containerIds = ['PayPal-container-1', 'PayPal-container-2'];
    containerIds.forEach((containerId) => {
      const containers = document.querySelectorAll(`[id="${containerId}"]`);

      containers.forEach((container) => {
        // @ts-ignore Paypal module has badly defined type here
        window.paypal.Buttons(paypalButtonConfig).render(container).catch((error) => {
          setErrorMessage('Error creating PayPal button: ' + error);
          throw new Error(error);
        });
      });
    });
    setHasPaypalButtonRendered(true)
  }

  const createBraintreePaypal = async (ppData: PaymentMethodSetupData) => {
    try {
      if (!hasPaypalButtonRendered) {
        const instance = await createBraintreePaypalInstance()

        if (!instance) {
          throw Error('Error connecting to braintree')
        }

        await instance.loadPayPalSDK({
          currency: 'GBP',
          intent: Intent.Authorize,
          vault: false,
          locale: 'en_GB',
          'enable-funding': 'card,credit,paylater'
        }, async (loadPayPalSDKErr) => {
          if (loadPayPalSDKErr) {
            console.error('Error loading PayPal SDK:', loadPayPalSDKErr);
            return;
          }
          renderPayPalButtons(ppData, instance)
        });
      }
    } catch (error) {
      console.error(error)
    }
  }

  return {
    initPaypalButtons,
    hasPaypalButtonRendered
  }
}
